import {
  getState,
  removeAudioTrack,
  removeGuide,
  removeKeyframe,
  setAutoKeyframe,
  setSelectedAudioId,
  setSelectedGuideId,
  snapshotKeyframe,
  subscribe,
  updateAudioTrack,
  updateGuide,
  updateKeyframe,
  updateLayer,
  updateLayerTransform,
} from '../store/project';
import { ensureAudioDuration, getCachedDuration, subscribeDuration } from '../audio/duration';
import { ensureLoaded, getAllFonts, subscribeFonts } from '../fonts/registry';
import { FULL_PALETTE, addCustomSwatch, getCustomSwatches } from '../ui/palette';
import type {
  Animation,
  AnimationType,
  AudioTrack,
  Easing,
  Guide,
  Layer,
  SlideDirection,
} from '../types';

let openFontModal: () => void = () => {};
export function setFontModalOpener(fn: () => void): void {
  openFontModal = fn;
}

const ANIM_TYPES: AnimationType[] = ['fade', 'slide', 'pop', 'typing', 'strike'];
const SLIDE_DIRS: SlideDirection[] = ['left', 'right', 'top', 'bottom'];

// (Render-lock machinery used to gate drags against the inspector rebuild.
// We've since switched every drag to a "defer-commit-to-mouseup" pattern
// — no store writes during the gesture, so the inspector simply doesn't
// re-render in the first place. The locks were removed.)

export class Inspector {
  private root: HTMLElement;
  // Set when render() bailed because the user was typing in one of our own
  // fields. Flushed on focusout so skipped updates (keyframe markers etc.)
  // catch up once editing ends.
  private deferredRebuild = false;

  constructor(root: HTMLElement) {
    this.root = root;
    subscribe(() => this.render());
    // When a song duration discovery resolves, re-render so the scrubber
    // lane jumps from the estimated 5-min fallback to the real number.
    subscribeDuration(() => this.render());
    // Flush a skipped rebuild once focus leaves an inspector control, or once
    // a color-picker <details> closes. Defer a frame so document.activeElement
    // / [open] state has settled. (toggle doesn't bubble → capture phase.)
    const flush = (): void => {
      requestAnimationFrame(() => {
        if (this.deferredRebuild) this.render();
      });
    };
    this.root.addEventListener('focusout', flush);
    this.root.addEventListener('toggle', flush, true);
  }

  private render(): void {
    // Don't rebuild while the user is mid-edit. emit() fires synchronously on
    // every keystroke / pick (updateLayer → emit → this listener), and
    // replaceChildren() below would destroy the live control — killing focus,
    // caret, in-flight 한글/IME composition, an open <select>, or an open color
    // picker. We skip the rebuild in two cases and flush it later:
    //   1. an editing control (input / textarea / select) holds focus
    //   2. a color-picker <details> is open (HSV / palette drags fire emit but
    //      don't keep focus, so the focus check alone wouldn't catch them)
    // Store + canvas already reflect the change; focusout / details-close flush.
    const active = document.activeElement;
    const focusedControl =
      active instanceof HTMLElement && this.root.contains(active) && isEditingControl(active);
    const colorPickerOpen =
      this.root.querySelector('details.color-picker-rich[open], details.layer-color-picker[open]') !== null;
    if (focusedControl || colorPickerOpen) {
      this.deferredRebuild = true;
      return;
    }
    this.deferredRebuild = false;

    const { project, selectedId, selectedAudioId, selectedGuideId } = getState();
    // Preserve scroll position across rebuilds — otherwise tweaking a
    // blend-mode (or any inspector field that fires emit()) bounces the
    // panel back to the top.
    const prevScroll = this.root.scrollTop;
    this.root.replaceChildren();

    // Guide inspector — when a canvas guide is selected, only its panel
    // shows. Cleared by selecting a layer / audio.
    if (selectedGuideId) {
      const guide = (project.guides ?? []).find((g) => g.id === selectedGuideId);
      if (guide) {
        this.root.appendChild(guideInspectorSection(guide));
        this.root.scrollTop = prevScroll;
        return;
      }
    }

    // Audio inspector takes precedence — selectedAudioId implies "no layer
    // selected" by store contract.
    if (selectedAudioId) {
      const audio = (project.audioTracks ?? []).find((a) => a.id === selectedAudioId);
      if (audio) {
        this.root.appendChild(audioInspectorSection(audio));
        this.root.scrollTop = prevScroll;
        return;
      }
    }

    const layer = project.layers.find((l) => l.id === selectedId);
    if (!layer) {
      const empty = document.createElement('div');
      empty.className = 'empty';
      empty.textContent = '레이어를 선택하면 속성이 보여요';
      this.root.appendChild(empty);
      this.root.scrollTop = 0;
      return;
    }

    this.root.appendChild(this.keyframeSection(layer));
    this.root.appendChild(this.metaSection(layer));
    this.root.appendChild(this.transformSection(layer));
    if (layer.kind === 'text') this.root.appendChild(this.textSection(layer));
    if (layer.kind === 'image' || layer.kind === 'video') this.root.appendChild(this.mediaSection(layer));
    if (layer.kind === 'shape') this.root.appendChild(this.shapeSection(layer));
    this.root.appendChild(this.effectsSection(layer));
    this.root.appendChild(this.timingSection(layer));
    this.root.appendChild(this.animationSection(layer, 'enter'));
    this.root.appendChild(this.animationSection(layer, 'exit'));
    this.root.scrollTop = prevScroll;
  }

  private keyframeSection(layer: Layer): HTMLElement {
    const sec = section('◇ 키프레임');
    const { playhead, autoKeyframe } = getState();
    const kfs = layer.keyframes ?? [];
    const active = kfs.find((k) => Math.abs(k.t - playhead) < 30);

    const auto = document.createElement('label');
    auto.className = 'kf-auto';
    const cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.checked = autoKeyframe;
    cb.addEventListener('change', () => setAutoKeyframe(cb.checked));
    auto.append(cb, document.createTextNode(' 자동 키프레임 (인스펙터 변경 자동 기록)'));
    sec.appendChild(auto);

    const snapBtn = document.createElement('button');
    snapBtn.className = 'btn primary';
    snapBtn.style.width = '100%';
    snapBtn.style.marginTop = '8px';
    snapBtn.textContent = active
      ? `◇ ${formatT(active.t)} 키프레임 갱신`
      : `◇ ${formatT(playhead)} 시점에 키프레임 추가`;
    snapBtn.addEventListener('click', () => snapshotKeyframe(layer.id, playhead));
    sec.appendChild(snapBtn);

    if (active) {
      const easeRow = document.createElement('div');
      easeRow.className = 'row';
      const lab = document.createElement('label');
      lab.textContent = 'ease';
      const sel = selectInput<Easing>(active.ease, ['linear', 'ease-in', 'ease-out', 'ease-in-out'], (v) =>
        updateKeyframe(layer.id, active.id, { ease: v }),
      );
      easeRow.append(lab, sel);
      sec.appendChild(easeRow);

      const del = document.createElement('button');
      del.className = 'btn ghost';
      del.style.width = '100%';
      del.style.marginTop = '4px';
      del.textContent = '✕ 이 키프레임 삭제';
      del.addEventListener('click', () => removeKeyframe(layer.id, active.id));
      sec.appendChild(del);
    }

    if (kfs.length > 0) {
      const list = document.createElement('div');
      list.className = 'kf-list';
      list.textContent = `전체: ${kfs.length}개 (${kfs.map((k) => formatT(k.t)).join(', ')})`;
      sec.appendChild(list);
    }
    return sec;
  }

  private metaSection(layer: Layer): HTMLElement {
    const sec = section(`레이어 (${layer.kind})`);
    sec.appendChild(
      row('이름', textInput(layer.name, (v) => updateLayer(layer.id, { name: v }))),
    );
    sec.appendChild(row('색상', layerColorPicker(layer.color, (v) => updateLayer(layer.id, { color: v }))));
    return sec;
  }

  private transformSection(layer: Layer): HTMLElement {
    const sec = section('변형');
    const t = layer.transform;
    sec.appendChild(
      rowSplit2(
        '위치',
        numberInput(t.x, (v) => updateLayerTransform(layer.id, { x: v }), { suffix: 'X' }),
        numberInput(t.y, (v) => updateLayerTransform(layer.id, { y: v }), { suffix: 'Y' }),
      ),
    );
    sec.appendChild(
      rowSplit2(
        '크기',
        numberInput(t.w, (v) => updateLayerTransform(layer.id, { w: v }), { suffix: 'W' }),
        numberInput(t.h, (v) => updateLayerTransform(layer.id, { h: v }), { suffix: 'H' }),
      ),
    );
    sec.appendChild(
      rowSplit2(
        '회전·투명',
        numberInput(t.rotate, (v) => updateLayerTransform(layer.id, { rotate: v }), { suffix: '°' }),
        numberInput(t.opacity, (v) => updateLayerTransform(layer.id, { opacity: clamp01(v) }), {
          step: 0.05,
          min: 0,
          max: 1,
        }),
      ),
    );
    return sec;
  }

  private textSection(layer: Extract<Layer, { kind: 'text' }>): HTMLElement {
    const sec = section('텍스트');
    sec.appendChild(
      row('내용', textareaInput(layer.text, (v) => updateLayer(layer.id, { text: v }))),
    );
    sec.appendChild(row('폰트', fontPicker(layer.fontFamily, (v) => updateLayer(layer.id, { fontFamily: v }))));
    sec.appendChild(
      rowSplit2(
        '굵기·크기',
        numberInput(layer.fontWeight, (v) => updateLayer(layer.id, { fontWeight: v }), {
          min: 100,
          max: 900,
          step: 100,
        }),
        numberInput(layer.fontSize, (v) => updateLayer(layer.id, { fontSize: v })),
      ),
    );
    sec.appendChild(
      rowSplit2(
        '자간·줄',
        numberInput(layer.letterSpacing, (v) => updateLayer(layer.id, { letterSpacing: v }), { step: 0.5 }),
        numberInput(layer.lineHeight, (v) => updateLayer(layer.id, { lineHeight: v }), { step: 0.05 }),
      ),
    );
    sec.appendChild(row('색상', colorInput(layer.color, (v) => updateLayer(layer.id, { color: v }))));
    sec.appendChild(
      row(
        '정렬',
        selectInput(layer.align, ['left', 'center', 'right'], (v) =>
          updateLayer(layer.id, { align: v as 'left' | 'center' | 'right' }),
        ),
      ),
    );
    return sec;
  }

  private mediaSection(layer: Extract<Layer, { kind: 'image' | 'video' }>): HTMLElement {
    const sec = section(layer.kind === 'image' ? '이미지' : '동영상');
    sec.appendChild(
      row('맞춤', selectInput(layer.fit, ['cover', 'contain'], (v) =>
        updateLayer(layer.id, { fit: v as 'cover' | 'contain' }),
      )),
    );
    if (layer.kind === 'video') {
      sec.appendChild(
        row('음소거', checkboxInput(layer.muted, (v) => updateLayer(layer.id, { muted: v }))),
      );
      sec.appendChild(
        row('반복', checkboxInput(layer.loop, (v) => updateLayer(layer.id, { loop: v }))),
      );
    }
    sec.appendChild(
      row(
        '교체',
        fileInput(layer.kind === 'image' ? 'image/*' : 'video/*', (dataUrl) =>
          updateLayer(layer.id, { src: dataUrl }),
        ),
      ),
    );
    return sec;
  }

  private shapeSection(layer: Extract<Layer, { kind: 'shape' }>): HTMLElement {
    const sec = section('도형');
    const shapeOptions = [
      'box', 'circle', 'triangle', 'star', 'diamond', 'arrow', 'heart', 'pentagon', 'hexagon', 'line',
    ] as const;
    sec.appendChild(
      row(
        '종류',
        selectInput(layer.shape, [...shapeOptions], (v) =>
          updateLayer(layer.id, { shape: v as Extract<Layer, { kind: 'shape' }>['shape'] }),
        ),
      ),
    );
    sec.appendChild(row('채움', colorInput(layer.fill, (v) => updateLayer(layer.id, { fill: v }))));
    sec.appendChild(row('선', colorInput(layer.stroke, (v) => updateLayer(layer.id, { stroke: v }))));
    sec.appendChild(
      row('선두께', numberInput(layer.strokeWidth, (v) => updateLayer(layer.id, { strokeWidth: v }))),
    );
    return sec;
  }

  private effectsSection(layer: Layer): HTMLElement {
    const sec = section('이펙트 (CSS)');
    const fx = layer.effects ?? {};
    const patch = (next: Partial<import('../types').LayerEffects>): void => {
      const merged = { ...(layer.effects ?? {}), ...next };
      // Strip "default" keys so the saved data stays compact.
      const cleaned: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(merged)) {
        if (v == null) continue;
        if (typeof v === 'number' && (v === 0 || (k === 'brightness' && v === 1) ||
            (k === 'contrast' && v === 1) || (k === 'saturate' && v === 1))) continue;
        cleaned[k] = v;
      }
      updateLayer(layer.id, { effects: cleaned as import('../types').LayerEffects });
    };

    // ─── Filters ───
    sec.appendChild(rangeRow('흐림 (px)', fx.blur ?? 0, 0, 40, 0.5,
      (v) => patch({ blur: v })));
    sec.appendChild(rangeRow('밝기', fx.brightness ?? 1, 0, 2, 0.05,
      (v) => patch({ brightness: v })));
    sec.appendChild(rangeRow('대비', fx.contrast ?? 1, 0, 2, 0.05,
      (v) => patch({ contrast: v })));
    sec.appendChild(rangeRow('채도', fx.saturate ?? 1, 0, 3, 0.05,
      (v) => patch({ saturate: v })));
    sec.appendChild(rangeRow('흑백', fx.grayscale ?? 0, 0, 1, 0.05,
      (v) => patch({ grayscale: v })));
    sec.appendChild(rangeRow('세피아', fx.sepia ?? 0, 0, 1, 0.05,
      (v) => patch({ sepia: v })));
    sec.appendChild(rangeRow('색조 (°)', fx.hueRotate ?? 0, -180, 180, 1,
      (v) => patch({ hueRotate: v })));
    sec.appendChild(rangeRow('반전', fx.invert ?? 0, 0, 1, 0.05,
      (v) => patch({ invert: v })));

    // ─── Shadow (box-shadow) ───
    const sh = fx.shadow ?? null;
    const enableShadow = checkboxInput(!!sh, (on) =>
      patch({ shadow: on
        ? { x: 0, y: 8, blur: 20, spread: 0, color: 'rgba(0,0,0,0.4)' }
        : undefined }),
    );
    sec.appendChild(row('그림자 사용', enableShadow));
    if (sh) {
      sec.appendChild(rowSplit2('x · y',
        numberInput(sh.x, (v) => patch({ shadow: { ...sh, x: v } })),
        numberInput(sh.y, (v) => patch({ shadow: { ...sh, y: v } })),
      ));
      sec.appendChild(rowSplit2('blur · spread',
        numberInput(sh.blur, (v) => patch({ shadow: { ...sh, blur: Math.max(0, v) } })),
        numberInput(sh.spread, (v) => patch({ shadow: { ...sh, spread: v } })),
      ));
      sec.appendChild(row('색상', colorInput(sh.color, (v) =>
        patch({ shadow: { ...sh, color: v } }))));
      sec.appendChild(row('inset',
        checkboxInput(!!sh.inset, (on) => patch({ shadow: { ...sh, inset: on } }))));
    }

    // ─── Drop-shadow (filter, follows alpha) ───
    const ds = fx.dropShadow ?? null;
    const enableDS = checkboxInput(!!ds, (on) =>
      patch({ dropShadow: on
        ? { x: 0, y: 4, blur: 12, color: 'rgba(0,0,0,0.5)' }
        : undefined }),
    );
    sec.appendChild(row('드롭쉐도우', enableDS));
    if (ds) {
      sec.appendChild(rowSplit2('x · y',
        numberInput(ds.x, (v) => patch({ dropShadow: { ...ds, x: v } })),
        numberInput(ds.y, (v) => patch({ dropShadow: { ...ds, y: v } })),
      ));
      sec.appendChild(row('blur',
        numberInput(ds.blur, (v) => patch({ dropShadow: { ...ds, blur: Math.max(0, v) } }))));
      sec.appendChild(row('색상', colorInput(ds.color, (v) =>
        patch({ dropShadow: { ...ds, color: v } }))));
    }

    // ─── Corner radius ───
    sec.appendChild(rangeRow('둥글기 (px)', fx.borderRadius ?? 0, 0, 200, 1,
      (v) => patch({ borderRadius: v })));

    // ─── Border ───
    const bd = fx.border ?? null;
    const enableBorder = checkboxInput(!!bd, (on) =>
      patch({ border: on ? { width: 2, color: '#ffffff', style: 'solid' } : undefined }),
    );
    sec.appendChild(row('테두리', enableBorder));
    if (bd) {
      sec.appendChild(row('두께 (px)',
        numberInput(bd.width, (v) => patch({ border: { ...bd, width: Math.max(0, v) } }))));
      sec.appendChild(row('색상', colorInput(bd.color, (v) => patch({ border: { ...bd, color: v } }))));
      sec.appendChild(row('스타일', selectInput(bd.style, ['solid', 'dashed', 'dotted'] as const,
        (v) => patch({ border: { ...bd, style: v } }))));
    }

    // ─── Blend mode ───
    const blends: NonNullable<import('../types').LayerEffects['blendMode']>[] = [
      'normal','multiply','screen','overlay','darken','lighten',
      'color-dodge','color-burn','hard-light','soft-light',
      'difference','exclusion','hue','saturation','color','luminosity',
    ];
    sec.appendChild(row('블렌드',
      selectInput(fx.blendMode ?? 'normal', blends, (v) =>
        patch({ blendMode: v === 'normal' ? undefined : v }))));

    // ─── Color overlay (Photoshop layer style) ───
    const co = fx.colorOverlay ?? null;
    sec.appendChild(row('색상 오버레이',
      checkboxInput(!!co, (on) => patch({
        colorOverlay: on
          ? { color: '#ff5a5f', opacity: 0.5, blendMode: 'normal' }
          : undefined,
      }))));
    if (co) {
      sec.appendChild(row('색상', colorInput(co.color, (v) =>
        patch({ colorOverlay: { ...co, color: v } }))));
      sec.appendChild(rangeRow('불투명도', co.opacity ?? 1, 0, 1, 0.05,
        (v) => patch({ colorOverlay: { ...co, opacity: v } })));
      sec.appendChild(row('블렌드',
        selectInput((co.blendMode ?? 'normal') as typeof blends[number], blends,
          (v) => patch({ colorOverlay: { ...co, blendMode: v } }))));
    }

    // ─── Gradient overlay ───
    const go = fx.gradientOverlay ?? null;
    sec.appendChild(row('그라데이션 오버레이',
      checkboxInput(!!go, (on) => patch({
        gradientOverlay: on ? {
          kind: 'linear',
          angle: 90,
          stops: [
            { color: '#ff5a5f', pos: 0 },
            { color: '#4ea8ff', pos: 1 },
          ],
          opacity: 0.7,
          blendMode: 'normal',
        } : undefined,
      }))));
    if (go) {
      sec.appendChild(row('타입',
        selectInput(go.kind, ['linear', 'radial'] as const,
          (v) => patch({ gradientOverlay: { ...go, kind: v } }))));
      if (go.kind === 'linear') {
        sec.appendChild(rangeRow('각도 (°)', go.angle ?? 90, 0, 360, 1,
          (v) => patch({ gradientOverlay: { ...go, angle: v } })));
      }
      const stops = go.stops ?? [];
      stops.forEach((s, idx) => {
        sec.appendChild(rowSplit2(`색 ${idx + 1}`,
          colorInput(s.color, (v) => {
            const next = stops.slice();
            next[idx] = { ...(next[idx] as { color: string; pos: number }), color: v };
            patch({ gradientOverlay: { ...go, stops: next } });
          }),
          numberInput(Math.round((s.pos ?? 0) * 100), (n) => {
            const next = stops.slice();
            next[idx] = { ...(next[idx] as { color: string; pos: number }), pos: Math.max(0, Math.min(1, n / 100)) };
            patch({ gradientOverlay: { ...go, stops: next } });
          }),
        ));
      });
      const stopRow = document.createElement('div');
      stopRow.style.display = 'grid';
      stopRow.style.gridTemplateColumns = '1fr 1fr';
      stopRow.style.gap = '4px';
      stopRow.style.marginBottom = '8px';
      const addStop = document.createElement('button');
      addStop.type = 'button';
      addStop.className = 'btn ghost';
      addStop.textContent = '+ 색 추가';
      addStop.addEventListener('click', () => {
        const next = stops.slice();
        const lastPos = stops.length > 0 ? (stops[stops.length - 1] as { pos: number }).pos : 0;
        next.push({ color: '#ffffff', pos: Math.min(1, lastPos + 0.25) });
        patch({ gradientOverlay: { ...go, stops: next } });
      });
      const delStop = document.createElement('button');
      delStop.type = 'button';
      delStop.className = 'btn ghost';
      delStop.textContent = '− 마지막 색 제거';
      delStop.disabled = stops.length <= 2;
      delStop.addEventListener('click', () => {
        if (stops.length <= 2) return;
        patch({ gradientOverlay: { ...go, stops: stops.slice(0, -1) } });
      });
      stopRow.append(addStop, delStop);
      sec.appendChild(stopRow);
      sec.appendChild(rangeRow('불투명도', go.opacity ?? 1, 0, 1, 0.05,
        (v) => patch({ gradientOverlay: { ...go, opacity: v } })));
      sec.appendChild(row('블렌드',
        selectInput((go.blendMode ?? 'normal') as typeof blends[number], blends,
          (v) => patch({ gradientOverlay: { ...go, blendMode: v } }))));
    }

    // ─── Reset ───
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.className = 'btn ghost';
    reset.style.width = '100%';
    reset.style.marginTop = '6px';
    reset.textContent = '↺ 이펙트 초기화';
    reset.addEventListener('click', () => updateLayer(layer.id, { effects: undefined }));
    sec.appendChild(reset);

    return sec;
  }

  private timingSection(layer: Layer): HTMLElement {
    const sec = section('시간 (ms)');
    sec.appendChild(
      rowSplit2(
        'start·end',
        numberInput(layer.start, (v) => updateLayer(layer.id, { start: Math.max(0, v) })),
        numberInput(layer.end, (v) =>
          updateLayer(layer.id, { end: Math.max(layer.start + 100, v) }),
        ),
      ),
    );
    return sec;
  }

  private animationSection(layer: Layer, key: 'enter' | 'exit'): HTMLElement {
    const sec = section(key === 'enter' ? '진입 애니메이션' : '퇴장 애니메이션');
    const current = layer[key];
    const types = ['none', ...ANIM_TYPES];
    const currentType = current?.type ?? 'none';
    sec.appendChild(
      row(
        '타입',
        selectInput(currentType, types, (v) => {
          if (v === 'none') {
            updateLayer(layer.id, { [key]: undefined } as Partial<Layer>);
          } else {
            const next: Animation = {
              type: v as AnimationType,
              duration: current?.duration ?? 400,
              ...(current && current.from ? { from: current.from } : {}),
              ...(current && current.to ? { to: current.to } : {}),
            };
            updateLayer(layer.id, { [key]: next } as Partial<Layer>);
          }
        }),
      ),
    );
    if (current) {
      sec.appendChild(
        row(
          '시간',
          numberInput(current.duration, (v) =>
            updateLayer(layer.id, { [key]: { ...current, duration: Math.max(0, v) } } as Partial<Layer>),
          ),
        ),
      );
      if (current.type === 'slide') {
        const dirKey = key === 'enter' ? 'from' : 'to';
        const value = (current[dirKey] as SlideDirection | undefined) ?? 'left';
        sec.appendChild(
          row(
            '방향',
            selectInput(value, SLIDE_DIRS, (v) => {
              const next: Animation = { ...current, [dirKey]: v as SlideDirection };
              updateLayer(layer.id, { [key]: next } as Partial<Layer>);
            }),
          ),
        );
      }
    }
    return sec;
  }
}

// ─── Field helpers ───
// Persistent open/closed state per section title — survives re-renders and
// page reloads, since render() rebuilds the inspector DOM frequently.
const SECTION_STORAGE_KEY = 'flash:inspector:closedSections';
const closedSections: Set<string> = (() => {
  try {
    const raw = localStorage.getItem(SECTION_STORAGE_KEY);
    if (raw) return new Set<string>(JSON.parse(raw) as string[]);
  } catch {
    /* ignore */
  }
  return new Set<string>();
})();
function persistClosedSections(): void {
  try {
    localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify([...closedSections]));
  } catch {
    /* ignore */
  }
}

function section(title: string): HTMLElement {
  // <details> gives free open/close; we drive the default-open via Set so
  // sections the user collapsed stay collapsed on every subsequent render.
  const s = document.createElement('details');
  s.className = 'section';
  s.open = !closedSections.has(title);
  s.dataset.title = title;
  const h = document.createElement('summary');
  h.className = 'section-title';
  h.textContent = title;
  s.appendChild(h);
  s.addEventListener('toggle', () => {
    if (s.open) closedSections.delete(title);
    else closedSections.add(title);
    persistClosedSections();
  });
  return s;
}

/** Range slider + numeric readout, sharing one onChange. The slider mutates
 *  only the number readout during drag and commits to the store on `change`
 *  (i.e. when the user releases the mouse) — same defer-commit pattern as
 *  the other drag handlers. The text input commits on every value change
 *  (typing is intentional). */
function rangeRow(
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  onCommit: (v: number) => void,
): HTMLElement {
  const r = document.createElement('div');
  r.className = 'row split2';
  const l = document.createElement('label');
  l.textContent = label;
  const range = document.createElement('input');
  range.type = 'range';
  range.min = String(min);
  range.max = String(max);
  range.step = String(step);
  range.value = String(value);
  const num = document.createElement('input');
  num.type = 'number';
  num.step = String(step);
  num.min = String(min);
  num.max = String(max);
  num.value = String(value);
  range.addEventListener('input', () => {
    num.value = range.value; // visual only
  });
  range.addEventListener('change', () => {
    const v = parseFloat(range.value);
    if (!Number.isNaN(v)) onCommit(v);
  });
  num.addEventListener('change', () => {
    const v = parseFloat(num.value);
    if (!Number.isNaN(v)) {
      range.value = num.value;
      onCommit(v);
    }
  });
  r.append(l, range, num);
  return r;
}

function row(label: string, control: HTMLElement): HTMLElement {
  const r = document.createElement('div');
  r.className = 'row';
  const l = document.createElement('label');
  l.textContent = label;
  r.append(l, control);
  return r;
}

function rowSplit2(label: string, a: HTMLElement, b: HTMLElement): HTMLElement {
  const r = document.createElement('div');
  r.className = 'row split2';
  const l = document.createElement('label');
  l.textContent = label;
  r.append(l, a, b);
  return r;
}

function fontPicker(value: string, onChange: (v: string) => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.display = 'grid';
  wrap.style.gridTemplateColumns = '1fr auto';
  wrap.style.gap = '4px';

  const select = document.createElement('select');

  const refresh = (): void => {
    select.replaceChildren();
    const all = getAllFonts();
    let saw = false;
    const addOption = (family: string): void => {
      const o = document.createElement('option');
      o.value = family;
      o.textContent = family;
      if (family === value) {
        o.selected = true;
        saw = true;
      }
      select.appendChild(o);
    };
    if (all.builtin.length) {
      const g = document.createElement('optgroup');
      g.label = 'Built-in';
      for (const f of all.builtin) {
        ensureLoaded(f);
        const o = document.createElement('option');
        o.value = f.family;
        o.textContent = f.family;
        if (f.family === value) {
          o.selected = true;
          saw = true;
        }
        g.appendChild(o);
      }
      select.appendChild(g);
    }
    if (all.user.length) {
      const g = document.createElement('optgroup');
      g.label = '내 폰트';
      for (const f of all.user) {
        ensureLoaded(f);
        const o = document.createElement('option');
        o.value = f.family;
        o.textContent = f.family;
        if (f.family === value) {
          o.selected = true;
          saw = true;
        }
        g.appendChild(o);
      }
      select.appendChild(g);
    }
    if (!saw && value) addOption(value);
  };
  refresh();
  subscribeFonts(refresh);

  select.addEventListener('change', () => onChange(select.value));

  const add = document.createElement('button');
  add.className = 'btn ghost';
  add.textContent = '+';
  add.title = '폰트 추가';
  add.style.padding = '4px 10px';
  add.addEventListener('click', () => openFontModal());

  wrap.append(select, add);
  return wrap;
}

/** Controls whose focus / open / caret state a full inspector rebuild would
 *  destroy — so we skip the rebuild while one of them is being edited. */
function isEditingControl(el: HTMLElement): boolean {
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) {
    return el.type === 'text' || el.type === 'number' || el.type === 'color';
  }
  return false;
}

/** Close a <details> popover when the user clicks outside it — the native
 *  element only closes on its own <summary>, so a color picker would otherwise
 *  stay open (and keep blocking inspector rebuilds) after the user moves on. */
function closeOnOutsideClick(details: HTMLDetailsElement): void {
  const onDown = (e: Event): void => {
    if (!details.contains(e.target as Node)) details.open = false;
  };
  details.addEventListener('toggle', () => {
    if (details.open) document.addEventListener('pointerdown', onDown, true);
    else document.removeEventListener('pointerdown', onDown, true);
  });
}

function textInput(value: string, onChange: (v: string) => void): HTMLElement {
  const i = document.createElement('input');
  i.type = 'text';
  i.value = value;
  i.addEventListener('input', () => onChange(i.value));
  return i;
}

function textareaInput(value: string, onChange: (v: string) => void): HTMLElement {
  const t = document.createElement('textarea');
  t.value = value;
  t.addEventListener('input', () => onChange(t.value));
  return t;
}

function numberInput(
  value: number,
  onChange: (v: number) => void,
  opts: { min?: number; max?: number; step?: number; suffix?: string } = {},
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.style.display = 'flex';
  wrap.style.gap = '4px';
  wrap.style.alignItems = 'center';
  const i = document.createElement('input');
  i.type = 'number';
  i.value = String(round2(value));
  if (opts.min != null) i.min = String(opts.min);
  if (opts.max != null) i.max = String(opts.max);
  i.step = String(opts.step ?? 1);
  i.style.flex = '1';
  i.addEventListener('input', () => {
    const v = parseFloat(i.value);
    if (!Number.isNaN(v)) onChange(v);
  });
  wrap.appendChild(i);
  if (opts.suffix) {
    const tag = document.createElement('span');
    tag.textContent = opts.suffix;
    tag.style.color = 'var(--ink-dim)';
    tag.style.fontSize = '11px';
    wrap.appendChild(tag);
  }
  return wrap;
}

function layerColorPicker(
  value: string | undefined,
  onChange: (v: string | undefined) => void,
): HTMLElement {
  // Collapsible: palette + HSV + custom row are hidden behind a <details>.
  // Closed by default — open on user request.
  const wrap = document.createElement('details');
  wrap.className = 'layer-color-picker cpk-details';

  const summary = document.createElement('summary');
  summary.className = 'cpk-summary';
  const summarySw = document.createElement('span');
  summarySw.className = 'cpk-summary-sw';
  summarySw.style.background = value || 'transparent';
  if (!value) summarySw.classList.add('empty');
  const summaryHex = document.createElement('span');
  summaryHex.className = 'cpk-summary-hex';
  summaryHex.textContent = value || '색 없음';
  summary.append(summarySw, summaryHex);
  wrap.appendChild(summary);

  const palette = document.createElement('div');
  palette.className = 'lcp-row';
  const all = [...FULL_PALETTE, ...getCustomSwatches()];
  for (const c of all) {
    const sw = document.createElement('button');
    sw.type = 'button';
    sw.className = 'lcp-sw';
    sw.style.background = c;
    if (c === value) sw.classList.add('active');
    sw.title = c;
    sw.dataset.color = c;
    palette.appendChild(sw);
  }

  // Palette drag: pointerdown on a swatch starts a "swipe". We track the
  // active swatch with a CSS class only — no store write per frame — and
  // commit a single onChange on pointerup. That keeps the inspector
  // (and the canvas) from re-rendering on every mousemove.
  let pendingColor: string | undefined = value;
  const setActive = (c: string): void => {
    pendingColor = c;
    palette.querySelectorAll('.lcp-sw').forEach((b) => {
      (b as HTMLElement).classList.toggle('active', (b as HTMLElement).dataset.color === c);
    });
  };
  const previewAt = (clientX: number, clientY: number): void => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const swEl = el?.closest('.lcp-sw') as HTMLElement | null;
    const c = swEl?.dataset.color;
    if (c) setActive(c);
  };
  palette.addEventListener('pointerdown', (e) => {
    const swEl = (e.target as HTMLElement).closest('.lcp-sw') as HTMLElement | null;
    if (!swEl?.dataset.color) return;
    e.preventDefault();
    setActive(swEl.dataset.color);
    const onMove = (ev: PointerEvent): void => previewAt(ev.clientX, ev.clientY);
    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      // Single commit on release.
      if (pendingColor && pendingColor !== value) onChange(pendingColor);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  });

  const hsv = makeHsvPicker(value || '#888888', (hex) => onChange(hex));

  const customRow = document.createElement('div');
  customRow.className = 'lcp-row';
  const customColor = document.createElement('input');
  customColor.type = 'color';
  customColor.className = 'lcp-custom';
  customColor.value = value && /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#888888';
  customColor.title = '직접 색상 추가';
  // Native color picker — the popup is browser-managed, so we don't need
  // to lock the inspector during it (the swatch DOM stays valid even if
  // the inspector re-renders behind the popup).
  customColor.addEventListener('input', () => onChange(customColor.value));
  customColor.addEventListener('change', () => addCustomSwatch(customColor.value));
  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'lcp-clear';
  clearBtn.textContent = '색 없음';
  clearBtn.addEventListener('click', () => onChange(undefined));
  customRow.append(customColor, clearBtn);

  wrap.append(palette, hsv, customRow);
  closeOnOutsideClick(wrap);
  return wrap;
}

function colorInput(value: string | undefined, onChange: (v: string) => void): HTMLElement {
  // A layer with no color set still needs *some* baseline so the HSV picker
  // and swatches don't blow up reading .match / .toLowerCase on undefined.
  const safeValue = value && typeof value === 'string' ? value : '#000000';
  const wrap = document.createElement('details');
  wrap.className = 'color-picker-rich';

  const summary = document.createElement('summary');
  summary.className = 'cpk-summary';
  const summarySw = document.createElement('span');
  summarySw.className = 'cpk-summary-sw';
  summarySw.style.background = safeValue;
  const summaryHex = document.createElement('span');
  summaryHex.className = 'cpk-summary-hex';
  summaryHex.textContent = safeValue;
  summary.append(summarySw, summaryHex);
  wrap.appendChild(summary);

  const topRow = document.createElement('div');
  topRow.className = 'cpk-top';
  const swatch = document.createElement('input');
  swatch.type = 'color';
  swatch.value = toColorInputValue(safeValue);
  swatch.className = 'cpk-native';
  const text = document.createElement('input');
  text.type = 'text';
  text.value = safeValue;
  text.className = 'cpk-hex';

  const apply = (v: string): void => {
    text.value = v;
    swatch.value = toColorInputValue(v);
    summarySw.style.background = v;
    summaryHex.textContent = v;
    onChange(v);
  };
  // Native color popup is browser-managed — no inspector lock needed.
  swatch.addEventListener('input', () => apply(swatch.value));
  text.addEventListener('input', () => {
    summarySw.style.background = text.value;
    summaryHex.textContent = text.value;
    onChange(text.value);
  });

  topRow.append(swatch, text);

  const W = window as unknown as { EyeDropper?: new () => { open(): Promise<{ sRGBHex: string }> } };
  if (typeof W.EyeDropper === 'function') {
    const drop = document.createElement('button');
    drop.type = 'button';
    drop.className = 'cpk-eyedropper';
    drop.title = '스포이드 — 화면에서 색 추출';
    drop.textContent = '⩃';
    drop.addEventListener('click', async () => {
      try {
        const ed = new W.EyeDropper!();
        const r = await ed.open();
        apply(r.sRGBHex);
        addCustomSwatch(r.sRGBHex);
      } catch {
        /* user cancelled */
      }
    });
    topRow.appendChild(drop);
  }

  wrap.append(topRow);

  wrap.appendChild(makeHsvPicker(safeValue, (v) => apply(v)));

  const presets = document.createElement('div');
  presets.className = 'cpk-presets';
  const renderPresets = (): void => {
    presets.replaceChildren();
    const all = [...FULL_PALETTE, ...getCustomSwatches()];
    const cmp = safeValue.toLowerCase();
    for (const c of all) {
      const sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'lcp-sw';
      sw.style.background = c;
      sw.title = c;
      sw.dataset.color = c;
      if (c.toLowerCase() === cmp) sw.classList.add('active');
      presets.appendChild(sw);
    }
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'cpk-add-preset';
    add.title = '현재 색을 프리셋에 추가';
    add.textContent = '+';
    add.addEventListener('click', () => {
      addCustomSwatch(text.value);
      renderPresets();
    });
    presets.appendChild(add);
  };
  renderPresets();
  // Drag through preset swatches — defer the commit. We update the visible
  // hex / summary / native swatch with applyVisual (no store write), then
  // run apply() once on pointerup.
  const applyVisual = (v: string): void => {
    text.value = v;
    swatch.value = toColorInputValue(v);
    summarySw.style.background = v;
    summaryHex.textContent = v;
  };
  let pendingPreset: string | null = null;
  const previewAt = (clientX: number, clientY: number): void => {
    const el = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
    const swEl = el?.closest('.lcp-sw') as HTMLElement | null;
    const c = swEl?.dataset.color;
    if (c) {
      pendingPreset = c;
      applyVisual(c);
    }
  };
  presets.addEventListener('pointerdown', (e) => {
    const swEl = (e.target as HTMLElement).closest('.lcp-sw') as HTMLElement | null;
    if (!swEl?.dataset.color) return;
    e.preventDefault();
    pendingPreset = swEl.dataset.color;
    applyVisual(pendingPreset);
    const onMove = (ev: PointerEvent): void => previewAt(ev.clientX, ev.clientY);
    const onUp = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (pendingPreset) apply(pendingPreset);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  });
  wrap.appendChild(presets);

  closeOnOutsideClick(wrap);
  return wrap;
}

function makeHsvPicker(initialHex: string | undefined, onChange: (hex: string) => void): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'cpk-hsv';

  const svBox = document.createElement('div');
  svBox.className = 'cpk-sv';
  const svPin = document.createElement('div');
  svPin.className = 'cpk-pin';
  svBox.appendChild(svPin);

  const hueBar = document.createElement('div');
  hueBar.className = 'cpk-hue';
  const huePin = document.createElement('div');
  huePin.className = 'cpk-hue-pin';
  hueBar.appendChild(huePin);

  let { h, s, v } = hexToHsv(initialHex);
  const sync = (): void => {
    svBox.style.background =
      `linear-gradient(to top, #000, transparent),` +
      ` linear-gradient(to right, #fff, hsl(${h * 360},100%,50%))`;
    svPin.style.left = `${s * 100}%`;
    svPin.style.top = `${(1 - v) * 100}%`;
    huePin.style.left = `${h * 100}%`;
  };
  sync();

  const emit = (): void => onChange(hsvToHex(h, s, v));

  // Drag handlers register on `window` instead of the box itself — that way
  // a fast drag that leaves the box doesn't drop the gesture. We only
  // update the visible pins during the drag and fire `onChange` once on
  // pointerup, so the inspector / canvas don't churn per frame.
  const previewSv = (e: PointerEvent): void => {
    const r = svBox.getBoundingClientRect();
    s = clamp01((e.clientX - r.left) / r.width);
    v = clamp01(1 - (e.clientY - r.top) / r.height);
    sync();
  };
  svBox.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    previewSv(e);
    const move = (ev: PointerEvent): void => previewSv(ev);
    const up = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      emit(); // single commit at the end of the gesture
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  });

  const previewHue = (e: PointerEvent): void => {
    const r = hueBar.getBoundingClientRect();
    h = clamp01((e.clientX - r.left) / r.width);
    sync();
  };
  hueBar.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    previewHue(e);
    const move = (ev: PointerEvent): void => previewHue(ev);
    const up = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      emit();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  });

  wrap.append(svBox, hueBar);
  return wrap;
}

function hexToHsv(hex: string | undefined | null): { h: number; s: number; v: number } {
  if (!hex || typeof hex !== 'string') return { h: 0, s: 0, v: 0 };
  const m = hex.match(/^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/);
  if (!m) return { h: 0, s: 0, v: 0 };
  let raw = m[1] as string;
  if (raw.length === 3) raw = raw.split('').map((c) => c + c).join('');
  const r = parseInt(raw.slice(0, 2), 16) / 255;
  const g = parseInt(raw.slice(2, 4), 16) / 255;
  const b = parseInt(raw.slice(4, 6), 16) / 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const d = mx - mn;
  let h = 0;
  if (d > 0) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = ((h * 60 + 360) % 360) / 360;
  }
  const s = mx === 0 ? 0 : d / mx;
  return { h, s, v: mx };
}

function hsvToHex(h: number, s: number, v: number): string {
  const c = v * s;
  const hp = h * 6;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (0 <= hp && hp < 1) { r = c; g = x; }
  else if (1 <= hp && hp < 2) { r = x; g = c; }
  else if (2 <= hp && hp < 3) { g = c; b = x; }
  else if (3 <= hp && hp < 4) { g = x; b = c; }
  else if (4 <= hp && hp < 5) { r = x; b = c; }
  else { r = c; b = x; }
  const m = v - c;
  const to = (n: number): string => {
    const vv = Math.round((n + m) * 255);
    return vv.toString(16).padStart(2, '0');
  };
  return `#${to(r)}${to(g)}${to(b)}`;
}

function selectInput<T extends string>(value: T, options: T[], onChange: (v: T) => void): HTMLElement {
  const s = document.createElement('select');
  for (const o of options) {
    const opt = document.createElement('option');
    opt.value = o;
    opt.textContent = o;
    s.appendChild(opt);
  }
  s.value = value;
  s.addEventListener('change', () => onChange(s.value as T));
  return s;
}

function checkboxInput(value: boolean, onChange: (v: boolean) => void): HTMLElement {
  const wrap = document.createElement('label');
  wrap.style.display = 'flex';
  wrap.style.gap = '6px';
  wrap.style.alignItems = 'center';
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.checked = value;
  cb.addEventListener('change', () => onChange(cb.checked));
  wrap.appendChild(cb);
  return wrap;
}

function fileInput(accept: string, onLoad: (dataUrl: string) => void): HTMLElement {
  const i = document.createElement('input');
  i.type = 'file';
  i.accept = accept;
  i.addEventListener('change', () => {
    const file = i.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onLoad(reader.result as string);
    reader.readAsDataURL(file);
  });
  return i;
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
function round2(x: number): number {
  return Math.round(x * 100) / 100;
}
function formatT(ms: number): string {
  const total = Math.max(0, Math.round(ms));
  const m = Math.floor(total / 60000);
  const s = Math.floor((total % 60000) / 1000);
  const mmm = total % 1000;
  return `${m}:${String(s).padStart(2, '0')}.${String(mmm).padStart(3, '0')}`;
}
function toColorInputValue(v: string): string {
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
  return '#000000';
}

// ─── Guide inspector ─────────────────────────────────────────────────────
function guideInspectorSection(guide: Guide): HTMLElement {
  const sec = section(`📐 가이드 (${guide.orientation === 'vertical' ? '세로' : '가로'})`);

  // Position readout — read-only, just so the user has context.
  const posInput = numberInput(Math.round(guide.position), (v) =>
    updateGuide(guide.id, { position: Math.max(0, v) }),
  );
  sec.appendChild(audioRow('위치 (px)', posInput));

  // Width
  sec.appendChild(audioRow('두께 (px)',
    numberInput(guide.width ?? 1, (v) =>
      updateGuide(guide.id, { width: Math.max(1, Math.min(20, Math.round(v))) }),
    ),
  ));

  // Color
  sec.appendChild(audioRow('색상',
    colorInput(guide.color ?? '#5cd0ff', (v) => updateGuide(guide.id, { color: v })),
  ));

  // Visibility
  const visible = guide.visible !== false;
  sec.appendChild(audioRow('보이기',
    checkboxInput(visible, (on) => updateGuide(guide.id, { visible: on })),
  ));
  if (!visible) {
    const note = document.createElement('div');
    note.style.fontSize = '11px';
    note.style.color = 'var(--ink-mute)';
    note.style.padding = '4px 0';
    note.textContent = '안 보여도 드래그할 때 스냅은 그대로 작동해요.';
    sec.appendChild(note);
  }

  // Actions
  const actions = document.createElement('div');
  actions.style.display = 'flex';
  actions.style.gap = '8px';
  actions.style.marginTop = '6px';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn ghost';
  close.textContent = '인스펙터 닫기';
  close.addEventListener('click', () => setSelectedGuideId(null));
  const del = document.createElement('button');
  del.type = 'button';
  del.className = 'btn';
  del.style.background = '#c0353a';
  del.style.color = 'white';
  del.textContent = '지우기';
  del.addEventListener('click', () => {
    removeGuide(guide.id);
    setSelectedGuideId(null);
  });
  actions.append(close, del);
  sec.appendChild(actions);

  return sec;
}

// ─── Audio inspector ─────────────────────────────────────────────────────
function audioInspectorSection(audio: AudioTrack): HTMLElement {
  const kindLabel =
    audio.kind === 'youtube' ? 'YouTube'
    : audio.kind === 'file' ? '파일 (MP3 등)'
    : audio.kind === 'soundcloud' ? 'SoundCloud'
    : 'Spotify';
  // Reuse the standard collapsible section so the audio panel folds the
  // same way layer sections do.
  const sec = section(`🔊 사운드 (${kindLabel})`);

  // Label
  sec.appendChild(audioRow('이름',
    textInput(audio.label || '', (v) => updateAudioTrack(audio.id, { label: v || undefined })),
  ));

  // Source readout (read-only) — gives the user something to verify against.
  if (audio.kind === 'youtube') {
    sec.appendChild(audioRow('영상 ID', readOnlyInput(audio.videoId)));
  } else if (audio.kind === 'file') {
    sec.appendChild(audioRow('파일', readOnlyInput(audio.name || '(파일)')));
  } else if (audio.kind === 'soundcloud' || audio.kind === 'spotify') {
    sec.appendChild(audioRow('URL', readOnlyInput(audio.url)));
    const warn = document.createElement('div');
    warn.style.fontSize = '11px';
    warn.style.color = 'var(--warn)';
    warn.style.lineHeight = '1.5';
    warn.style.padding = '6px 0';
    warn.textContent =
      audio.kind === 'spotify'
        ? '⚠ Spotify는 현재 재생되지 않습니다 (정보만 저장). 실제 재생은 다음 업데이트.'
        : '⚠ SoundCloud는 현재 재생되지 않습니다 (정보만 저장). 실제 재생은 다음 업데이트.';
    sec.appendChild(warn);
  }

  // Numeric start/end (seconds) — keep both — user asked for "초로도 되지만".
  const startInput = numberInputSec(audio.songStart, (ms) =>
    updateAudioTrack(audio.id, { songStart: Math.max(0, Math.min(ms, audio.songEnd - 100)) }),
  );
  const endInput = numberInputSec(audio.songEnd, (ms) =>
    updateAudioTrack(audio.id, { songEnd: Math.max(audio.songStart + 100, ms) }),
  );
  sec.appendChild(audioRow('시작 (s)', startInput));
  sec.appendChild(audioRow('끝 (s)', endInput));

  // Scrubber — visual range slider showing songStart/songEnd inside the song.
  // We try to discover the real duration (file via Audio(), YouTube via the
  // IFrame API). Until that resolves, fall back to a generous lane.
  const cached = getCachedDuration(audio);
  const songDur = cached ?? inferSongDuration(audio);
  const scrubber = audioScrubber(audio, songDur, cached != null);
  sec.appendChild(scrubber);
  if (cached == null && (audio.kind === 'file' || audio.kind === 'youtube')) {
    void ensureAudioDuration(audio); // resolves into cache; subscribeDuration triggers re-render
  }

  // Volume
  const vol = document.createElement('input');
  vol.type = 'range';
  vol.min = '0';
  vol.max = '1';
  vol.step = '0.01';
  vol.value = String(audio.volume);
  vol.style.width = '100%';
  vol.addEventListener('input', () => updateAudioTrack(audio.id, { volume: parseFloat(vol.value) }));
  sec.appendChild(audioRow('볼륨', vol));

  // Fade in / out
  const fadeIn = numberInputMs(audio.fadeIn ?? 0, (ms) => updateAudioTrack(audio.id, { fadeIn: ms || undefined }));
  const fadeOut = numberInputMs(audio.fadeOut ?? 0, (ms) => updateAudioTrack(audio.id, { fadeOut: ms || undefined }));
  sec.appendChild(audioRow('페이드 인 (ms)', fadeIn));
  sec.appendChild(audioRow('페이드 아웃 (ms)', fadeOut));

  // Actions
  const actions = document.createElement('div');
  actions.style.display = 'flex';
  actions.style.gap = '8px';
  actions.style.marginTop = '6px';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn ghost';
  close.textContent = '인스펙터 닫기';
  close.addEventListener('click', () => setSelectedAudioId(null));
  const del = document.createElement('button');
  del.type = 'button';
  del.className = 'btn';
  del.style.background = '#c0353a';
  del.style.color = 'white';
  del.textContent = '삭제';
  del.addEventListener('click', () => {
    if (window.confirm('이 사운드 트랙을 삭제할까요?')) {
      removeAudioTrack(audio.id);
      setSelectedAudioId(null);
    }
  });
  actions.append(close, del);
  sec.appendChild(actions);

  return sec;
}

function audioRow(label: string, body: HTMLElement): HTMLElement {
  const r = document.createElement('div');
  r.className = 'row';
  const l = document.createElement('label');
  l.textContent = label;
  const wrap = document.createElement('div');
  wrap.appendChild(body);
  r.append(l, wrap);
  return r;
}

function readOnlyInput(value: string): HTMLInputElement {
  const i = document.createElement('input');
  i.type = 'text';
  i.value = value;
  i.readOnly = true;
  i.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, monospace';
  i.style.fontSize = '11px';
  return i;
}

function numberInputSec(ms: number, onChange: (ms: number) => void): HTMLInputElement {
  const i = document.createElement('input');
  i.type = 'number';
  i.step = '0.1';
  i.min = '0';
  i.value = (ms / 1000).toFixed(2);
  i.addEventListener('change', () => {
    const sec = parseFloat(i.value);
    if (!Number.isNaN(sec)) onChange(Math.round(sec * 1000));
  });
  return i;
}

function numberInputMs(ms: number, onChange: (ms: number) => void): HTMLInputElement {
  const i = document.createElement('input');
  i.type = 'number';
  i.step = '50';
  i.min = '0';
  i.value = String(ms);
  i.addEventListener('change', () => {
    const v = parseInt(i.value, 10);
    if (!Number.isNaN(v)) onChange(Math.max(0, v));
  });
  return i;
}

/** Best-effort song duration. We don't always know the real one (YouTube /
    SoundCloud / Spotify need API calls) so we lean conservative — far enough
    that the user can still drag the end handle right. */
function inferSongDuration(audio: AudioTrack): number {
  const known = audio.songEnd;
  return Math.max(known + 30000, 5 * 60 * 1000);
}

/** Scrubber: a horizontal lane representing the song from 0..songDur, with
    two draggable handles for songStart and songEnd. */
function audioScrubber(audio: AudioTrack, songDur: number, realDuration: boolean): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'audio-scrubber';

  const lengthNote = document.createElement('div');
  lengthNote.className = 'audio-scrubber-len';
  lengthNote.textContent = realDuration
    ? `곡 길이: ${formatSec(songDur)}`
    : `곡 길이 미상 — ${formatSec(songDur)} 추정`;
  wrap.appendChild(lengthNote);

  const lane = document.createElement('div');
  lane.className = 'audio-scrubber-lane';

  const range = document.createElement('div');
  range.className = 'audio-scrubber-range';

  const startH = document.createElement('div');
  startH.className = 'audio-scrubber-handle start';
  startH.title = '시작 위치 — 드래그';
  const endH = document.createElement('div');
  endH.className = 'audio-scrubber-handle end';
  endH.title = '끝 위치 — 드래그';

  lane.append(range, startH, endH);
  wrap.appendChild(lane);

  const tip = document.createElement('div');
  tip.className = 'audio-scrubber-tip';
  tip.textContent = `${formatSec(audio.songStart)} → ${formatSec(audio.songEnd)} · 길이 ${formatSec(audio.songEnd - audio.songStart)}`;
  wrap.appendChild(tip);

  // Local mirror of songStart/songEnd so the drag handler can chain reads
  // without mutating the store's audio object.
  let curStart = audio.songStart;
  let curEnd = audio.songEnd;

  const layout = (): void => {
    const sPct = (curStart / songDur) * 100;
    const ePct = Math.min(100, (curEnd / songDur) * 100);
    range.style.left = `${sPct}%`;
    range.style.width = `${Math.max(0, ePct - sPct)}%`;
    startH.style.left = `${sPct}%`;
    endH.style.left = `${ePct}%`;
    tip.textContent = `${formatSec(curStart)} → ${formatSec(curEnd)} · 길이 ${formatSec(curEnd - curStart)}`;
  };
  layout();

  const drag = (handle: HTMLElement, which: 'start' | 'end'): void => {
    handle.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const laneRect = lane.getBoundingClientRect();
      const move = (ev: PointerEvent): void => {
        const x = (ev.clientX - laneRect.left) / laneRect.width;
        const ms = Math.max(0, Math.min(songDur, x * songDur));
        if (which === 'start') curStart = Math.min(ms, curEnd - 100);
        else curEnd = Math.max(ms, curStart + 100);
        // Visual only — no store write per frame.
        layout();
      };
      const up = (): void => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        // Single commit on release.
        if (which === 'start') {
          updateAudioTrack(audio.id, { songStart: Math.round(curStart) });
        } else {
          updateAudioTrack(audio.id, { songEnd: Math.round(curEnd) });
        }
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    });
  };
  drag(startH, 'start');
  drag(endH, 'end');

  // Click on lane (not on a handle) jumps the nearest handle.
  lane.addEventListener('pointerdown', (e) => {
    if (e.target !== lane && e.target !== range) return;
    const laneRect = lane.getBoundingClientRect();
    const x = (e.clientX - laneRect.left) / laneRect.width;
    const ms = Math.max(0, Math.min(songDur, x * songDur));
    const distStart = Math.abs(ms - curStart);
    const distEnd = Math.abs(ms - curEnd);
    if (distStart < distEnd) {
      curStart = Math.min(ms, curEnd - 100);
      updateAudioTrack(audio.id, { songStart: Math.round(curStart) });
    } else {
      curEnd = Math.max(ms, curStart + 100);
      updateAudioTrack(audio.id, { songEnd: Math.round(curEnd) });
    }
    layout();
  });

  return wrap;
}

function formatSec(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
